// Setting up a second factor, for the session that needs one.
//
// GET /me says `mfaSatisfied: false` for an owner, reviewer or agency operator
// who signed in with a password alone, and every office route answers 403
// `mfa_required` until that changes (apps/api/assurance.ts — enforced on the
// role, with no switch). This used to be a screen with nothing to press,
// because nothing could issue a second factor. Now the API can, so this offers
// the one thing that resolves it:
//
//   1. POST /v1/auth/totp/enrolment — a secret, shown for an authenticator app.
//   2. POST /v1/auth/totp/confirmation — a code from it, and the password
//      again. The API ends this session and returns a new one at `aal2`, which
//      replaces it through `adoptSession` and is checked against GET /me first.
//
// The password is asked for again on purpose: a session alone must not be able
// to put a second factor on an account, or a stolen one could enrol the thief.
//
// No QR code. Drawing one needs a library or an encoder this app does not have,
// and an `otpauth://` link plus the key typed by hand is what every
// authenticator app accepts. The secret is shown once and kept only in this
// component's state.

import { useState, type ReactNode } from 'react';
import { ApiError, request } from '../api/client.ts';
import type { SessionGrant } from '../api/types.ts';
import { useWorkspace } from '../state/workspace.tsx';

interface Enrolment {
  readonly secret: string;
  readonly otpauthUri: string;
}

/** The key in groups of four, which is how people copy it without losing their place. */
function grouped(secret: string): string {
  return secret.replace(/(.{4})/g, '$1 ').trim();
}

export function SecondFactorSetup(): ReactNode {
  const workspace = useWorkspace();
  const [enrolment, setEnrolment] = useState<Enrolment | null>(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function start(): Promise<void> {
    setPending(true);
    setError(null);
    try {
      setEnrolment(
        await request<Enrolment>('/v1/auth/totp/enrolment', { method: 'POST', token: workspace.token }),
      );
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 409) {
        setError(
          'This account already has an authenticator set up. Sign out and sign in again with its code.',
        );
      } else if (caught instanceof ApiError && caught.status === 404) {
        setError(
          'Second factors cannot be set up on this deployment yet. Ask your bookkeeping office.',
        );
      } else if (caught instanceof ApiError && caught.status === 401) {
        workspace.signOut({ revoke: false });
      } else {
        setError('The authenticator could not be set up. Try again.');
      }
    } finally {
      setPending(false);
    }
  }

  async function confirm(): Promise<void> {
    setPending(true);
    setError(null);
    try {
      const grant = await request<SessionGrant>('/v1/auth/totp/confirmation', {
        method: 'POST',
        token: workspace.token,
        body: { totpCode: code, password },
      });
      setPassword('');
      await workspace.adoptSession(grant.token);
    } catch (caught) {
      setCode('');
      if (caught instanceof ApiError && caught.status === 422) {
        setError('That code is not right. Check the time on your phone and use the newest code.');
      } else if (caught instanceof ApiError && caught.status === 401) {
        setError('That password is not right.');
      } else if (caught instanceof ApiError && caught.status === 429) {
        setError('Too many attempts. Wait a few minutes before trying again.');
      } else {
        setError('The code could not be checked. Try again.');
      }
    } finally {
      setPending(false);
    }
  }

  if (enrolment === null) {
    return (
      <>
        {error === null ? null : (
          <p className="error-banner" role="alert">
            {error}
          </p>
        )}
        <button type="button" className="btn primary" disabled={pending} onClick={() => void start()}>
          {pending ? 'Preparing…' : 'Set up an authenticator app'}
        </button>
      </>
    );
  }

  const ready = /^[0-9]{6}$/.test(code) && password !== '';
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        if (ready && !pending) void confirm();
      }}
    >
      {error === null ? null : (
        <p className="error-banner" role="alert">
          {error}
        </p>
      )}
      <ol className="small" style={{ paddingLeft: 18, marginBottom: 16 }}>
        <li>
          Open your authenticator app and add an account.{' '}
          <a href={enrolment.otpauthUri}>Open in authenticator app</a> on this device, or enter this
          key by hand:
          <p>
            <code aria-label="Authenticator key">{grouped(enrolment.secret)}</code>
          </p>
        </li>
        <li>Type the six-digit code it shows, and your password.</li>
      </ol>
      <label className="field">
        Authentication code
        <input
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          value={code}
          onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
        />
      </label>
      <label className="field">
        Password
        <input
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
      </label>
      <button type="submit" className="btn primary" disabled={pending || !ready}>
        {pending ? 'Checking…' : 'Confirm and continue'}
      </button>
    </form>
  );
}
